// 009 (P-CART-1..9; FR-001..FR-045): the server's cart by child with steppers and Remove, the
// server's totals and split notice, the delivery address (default, Change, Add address), the mobile
// number when missing, the payment method, and Pay with every blocking reason. Web desktop shows the
// three-step header with cart and address side by side; phones scroll with a sticky Pay bar.
import { Link, useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";

import type { Api } from "@/api/types";
import { newIdempotencyKey } from "@/lib/idempotencyKey";
import { normaliseMobile } from "@/profile/validation";
import { addressVM } from "@/profile/viewModels";
import { useParentData } from "@/profile/useParentData";
import { useChromeKind } from "@/shell/chrome/useChromeKind";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { lineKey } from "@/shop/cartStore";
import { useCart } from "@/shop/useCart";
import { formatPaise } from "@/shop/format";
import { QuantityControl } from "@/shop/QuantityControl";
import { useShopping } from "@/shop/shopApi";
import { classSectionText } from "@/shop/useShopper";
import { makeStyles } from "@/theme/makeStyles";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";
import { TextField } from "@/ui/TextField";

import { checkoutMessage, CHECKOUT_COPY as C } from "./copy";
import { FakePaymentSheet } from "./FakePaymentSheet";
import { lastCheckout } from "./lastCheckout";
import { pay } from "./payFlow";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1, backgroundColor: t.color.background },
    page: {
      width: "100%",
      maxWidth: 1180,
      alignSelf: "center",
      padding: t.space.lg,
      gap: t.space.lg,
      paddingBottom: t.space.x3l,
    },
    steps: { flexDirection: "row", gap: t.space.md, flexWrap: "wrap" },
    columns: {
      flexDirection: "row",
      gap: t.space.x2l,
      alignItems: "flex-start",
    },
    main: { flex: 1, gap: t.space.lg },
    side: { width: 380, gap: t.space.lg },
    card: {
      borderRadius: t.radius.card,
      backgroundColor: t.color.surface,
      padding: t.space.lg,
      gap: t.space.sm,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      gap: t.space.md,
    },
    line: {
      flexDirection: "row",
      alignItems: "center",
      gap: t.space.md,
      paddingVertical: t.space.xs,
    },
    grow: { flex: 1, gap: 2 },
    choice: {
      minHeight: t.size.button,
      borderRadius: t.radius.button,
      borderWidth: t.borderWidth.input,
      borderColor: t.color.border,
      padding: t.space.md,
      alignItems: "flex-start",
    },
    choiceOn: { borderColor: t.color.accent, borderWidth: 2 },
    bar: {
      padding: t.space.lg,
      gap: t.space.xs,
      backgroundColor: t.color.background,
      borderTopWidth: 1,
      borderTopColor: t.color.border,
    },
  }),
);

type Method = Api["PaymentMethod"];

export function CartScreen() {
  const styles = useStyles();
  const router = useRouter();
  const wide = useChromeKind() === "desktop";
  const { online } = useNetworkStatus();
  const { client } = useShopping();
  const { cart, pending, error, actions } = useCart();
  const { profile, addresses, actions: parent } = useParentData();
  const [method, setMethod] = useState<Method>("UPI");
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [mobile, setMobile] = useState("");
  const [mobileError, setMobileError] = useState<string | null>(null);
  const [savingMobile, setSavingMobile] = useState(false);
  const [paying, setPaying] = useState<"idle" | "paying" | "confirming">(
    "idle",
  );
  const [message, setMessage] = useState<{
    tone: "error" | "info" | "warning";
    text: string;
    ref?: string | null;
    orders?: boolean;
  } | null>(null);
  // One key per attempt: a retry of the same attempt (same total, address, method) reuses it.
  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);

  useEffect(() => {
    void actions.load();
  }, [actions]);
  // The address card and the mobile check need the profile and addresses (a reload starts empty).
  const needParent = profile.data === null || addresses.data === null;
  useEffect(() => {
    if (needParent) void parent.refresh();
  }, [needParent, parent]);

  const list = useMemo(
    () => (addresses.data ?? []).map(addressVM),
    [addresses.data],
  );
  // A newly added address (Add address returns here) becomes the choice ("adjust state while
  // rendering": compare with the count seen last render).
  const [seenCount, setSeenCount] = useState<number | null>(null);
  if (addresses.data && seenCount !== list.length) {
    if (seenCount !== null && list.length > seenCount) {
      const newest = [...list].sort((a, b) =>
        b.raw.createdAt.localeCompare(a.raw.createdAt),
      )[0];
      if (newest) setChosenId(newest.id);
    }
    setSeenCount(list.length);
  }
  const address =
    list.find((a) => a.id === chosenId) ??
    list.find((a) => a.isDefault) ??
    list[0] ??
    null;
  const hasMobile = Boolean(profile.data?.mobile);

  if (!cart) {
    const failed = error?.key === "load" ? error.error : null;
    return (
      <View style={styles.root} testID="cart-screen">
        <View style={styles.page} role="main">
          {wide ? (
            <Text variant="display" accessibilityRole="header" aria-level={1}>
              {C.title}
            </Text>
          ) : null}
          {failed ? (
            <InlineMessage
              tone="error"
              detail={
                failed.requestId ? `Reference: ${failed.requestId}` : null
              }
              action={
                <Button variant="outline" onPress={() => void actions.load()}>
                  {C.retry}
                </Button>
              }
            >
              {C.loadFailed}
            </InlineMessage>
          ) : (
            <Text tone="muted">…</Text>
          )}
        </View>
      </View>
    );
  }

  const unavailable = cart.children.some((c) =>
    c.lines.some((l) => !l.available),
  );
  const blocked =
    cart.itemCount === 0
      ? C.blocked.empty
      : unavailable
        ? C.blocked.unavailable
        : !address
          ? C.blocked.noAddress
          : !hasMobile
            ? C.blocked.noMobile
            : !online
              ? C.blocked.offline
              : paying !== "idle"
                ? C.blocked.busy
                : null;

  async function saveMobile() {
    const n = normaliseMobile(mobile);
    if (!n.ok || n.value === null) {
      setMobileError(C.mobileInvalid);
      return;
    }
    setSavingMobile(true);
    try {
      await parent.updateProfile({ mobile: `+91${n.value}` });
      setMobileError(null);
    } catch {
      setMobileError(C.mobileInvalid);
    } finally {
      setSavingMobile(false);
    }
  }

  async function startPay() {
    if (blocked || !address || !cart) return;
    const body = {
      addressId: address.id,
      paymentMethod: method,
      expectedTotalInPaise: cart.totalInPaise,
    };
    const fingerprint = JSON.stringify(body);
    if (attempt.current?.fingerprint !== fingerprint)
      attempt.current = { fingerprint, key: newIdempotencyKey() };
    setMessage(null);
    setPaying("paying");
    const outcome = await pay(body, attempt.current.key, {
      client,
      onConfirming: () => setPaying("confirming"),
    });
    setPaying("idle");
    switch (outcome.type) {
      case "paid":
        attempt.current = null;
        lastCheckout.set(outcome.checkout, address.oneLine);
        void actions.load();
        router.replace("/order-placed");
        return;
      case "pending":
        attempt.current = null;
        setMessage({ tone: "info", text: C.stillConfirming, orders: true });
        return;
      case "underReview":
        attempt.current = null;
        setMessage({ tone: "warning", text: C.underReview, orders: true });
        return;
      case "expired":
        attempt.current = null;
        setMessage({ tone: "warning", text: C.expired });
        return;
      case "superseded":
        attempt.current = null;
        setMessage({ tone: "warning", text: C.superseded, orders: true });
        return;
      case "failed":
        attempt.current = null;
        setMessage({ tone: "error", text: C.failed });
        return;
      case "cancelled":
        setMessage({ tone: "info", text: C.cancelled });
        return;
      case "unavailable":
        setMessage({ tone: "error", text: C.noGateway });
        return;
      case "refused": {
        const m = checkoutMessage(outcome.error);
        setMessage({ tone: "error", text: m.text, ref: m.reference });
        if (
          outcome.error.code === "PRICE_CHANGED" ||
          outcome.error.code === "CART_HAS_UNAVAILABLE_ITEMS"
        ) {
          attempt.current = null;
          void actions.load();
        }
        if (outcome.error.code === "MOBILE_REQUIRED") void parent.refresh();
      }
    }
  }

  const children = (
    <View style={styles.main}>
      {cart.itemCount === 0 ? (
        <View style={styles.card}>
          <Text weight="semiBold">{C.empty}</Text>
          <Link href="/shop/SCHOOL_BOOKS" asChild>
            <Button variant="outline" accessibilityRole="link">
              {C.emptyAction}
            </Button>
          </Link>
        </View>
      ) : (
        cart.children.map((child) => (
          <View
            key={child.studentId}
            style={styles.card}
            role="region"
            aria-label={child.name}
          >
            <View style={styles.row}>
              <Text weight="bold">{`${child.name} · ${classSectionText(child.classLevel, child.section)}`}</Text>
              <Text weight="semiBold">
                {formatPaise(child.subtotalInPaise)}
              </Text>
            </View>
            {child.schoolName ? (
              <Text size="sm" tone="muted">
                {child.schoolName}
              </Text>
            ) : null}
            {child.lines.map((l) => {
              const key = lineKey(child.studentId, l.productId);
              return (
                <View
                  key={l.productId}
                  style={styles.line}
                  testID={`cart-line-${l.productId}`}
                >
                  <View style={styles.grow}>
                    <Text numberOfLines={2}>{l.name}</Text>
                    <Text size="sm" tone="muted">
                      {l.available
                        ? formatPaise(l.lineTotalInPaise)
                        : `${C.unavailable}${l.unavailableReason ? ` · ${l.unavailableReason}` : ""}`}
                    </Text>
                  </View>
                  {l.available ? (
                    <QuantityControl
                      name={l.name}
                      quantity={l.quantity}
                      pending={pending.has(key)}
                      disabled={!online || paying !== "idle"}
                      onChange={(q) =>
                        actions.setQuantity(child.studentId, l.productId, q)
                      }
                    />
                  ) : null}
                  <PressableTarget
                    accessibilityRole="button"
                    accessibilityLabel={`${C.remove} ${l.name}`}
                    disabled={!online || paying !== "idle"}
                    onPress={() =>
                      actions.setQuantity(child.studentId, l.productId, 0)
                    }
                  >
                    <Text size="sm" weight="semiBold" tone="primary">
                      {C.remove}
                    </Text>
                  </PressableTarget>
                </View>
              );
            })}
          </View>
        ))
      )}
      {error && error.key !== "load" ? (
        <InlineMessage tone="error">
          {checkoutMessage(error.error).text}
        </InlineMessage>
      ) : null}
    </View>
  );

  const side = (
    <View style={wide ? styles.side : styles.main}>
      <View style={styles.card} role="region" aria-label={C.summary}>
        <Text weight="bold" size="lg">
          {C.summary}
        </Text>
        <View style={styles.row}>
          <Text>{`${C.itemsTotal} (${C.items(cart.itemCount)})`}</Text>
          <Text>{formatPaise(cart.itemsTotalInPaise)}</Text>
        </View>
        <View style={styles.row}>
          <Text>{C.delivery}</Text>
          <Text>
            {cart.deliveryFeeInPaise
              ? formatPaise(cart.deliveryFeeInPaise)
              : C.free}
          </Text>
        </View>
        <View style={styles.row}>
          <Text weight="bold">{C.total}</Text>
          <Text weight="bold" testID="cart-total">
            {formatPaise(cart.totalInPaise)}
          </Text>
        </View>
        {cart.orderCount > 1 ? (
          <>
            <InlineMessage tone="info">
              {C.split(cart.orderCount)}
            </InlineMessage>
            {cart.orders.map((o) => (
              <View key={o.schoolId} style={styles.row}>
                <Text size="sm">{o.schoolName}</Text>
                <Text size="sm">{formatPaise(o.totalInPaise)}</Text>
              </View>
            ))}
          </>
        ) : null}
      </View>
      <View style={styles.card} role="region" aria-label={C.deliverTo}>
        <View style={styles.row}>
          <Text weight="bold">{C.deliverTo}</Text>
          {list.length > 1 ? (
            <PressableTarget
              accessibilityRole="button"
              onPress={() => setChoosing((v) => !v)}
            >
              <Text weight="semiBold" tone="primary">
                {C.change}
              </Text>
            </PressableTarget>
          ) : null}
        </View>
        {address ? (
          <>
            <Text weight="semiBold">{address.title}</Text>
            <Text tone="muted">{address.oneLine}</Text>
            <Text size="sm" tone="muted">
              {C.pinSaved}
            </Text>
          </>
        ) : (
          <Text tone="muted">{C.noAddress}</Text>
        )}
        {choosing ? (
          <View
            role="radiogroup"
            aria-label={C.chooseAddress}
            style={styles.main}
          >
            {list.map((a) => (
              <PressableTarget
                key={a.id}
                accessibilityRole="radio"
                accessibilityLabel={`${a.title}, ${a.oneLine}`}
                aria-checked={a.id === address?.id}
                accessibilityState={{ checked: a.id === address?.id }}
                onPress={() => {
                  setChosenId(a.id);
                  setChoosing(false);
                }}
                style={[styles.choice, a.id === address?.id && styles.choiceOn]}
              >
                <Text weight="semiBold">{a.title}</Text>
                <Text size="sm" tone="muted">
                  {a.oneLine}
                </Text>
              </PressableTarget>
            ))}
          </View>
        ) : null}
        <Link href="/addresses/new" asChild>
          <Button variant="outline" accessibilityRole="link">
            {C.addAddress}
          </Button>
        </Link>
      </View>
      {profile.data && !hasMobile ? (
        <View style={styles.card} role="region" aria-label={C.mobileTitle}>
          <Text weight="bold">{C.mobileTitle}</Text>
          <Text tone="muted">{C.mobileNeeded}</Text>
          <TextField
            label={C.mobileTitle}
            kind="tel"
            value={mobile}
            onChangeText={setMobile}
            error={mobileError}
          />
          <Button pending={savingMobile} onPress={() => void saveMobile()}>
            {C.mobileSave}
          </Button>
        </View>
      ) : null}
      <View style={styles.card} role="radiogroup" aria-label={C.payment}>
        <Text weight="bold">{C.payment}</Text>
        {(Object.keys(C.methods) as Method[]).map((m) => (
          <PressableTarget
            key={m}
            accessibilityRole="radio"
            accessibilityLabel={C.methods[m].label}
            aria-checked={m === method}
            accessibilityState={{ checked: m === method }}
            onPress={() => setMethod(m)}
            style={[styles.choice, m === method && styles.choiceOn]}
          >
            <Text weight="semiBold">{C.methods[m].label}</Text>
            <Text size="sm" tone="muted">
              {C.methods[m].helper}
            </Text>
          </PressableTarget>
        ))}
      </View>
      {wide ? payBlock() : null}
    </View>
  );

  function payBlock() {
    return (
      <View
        style={wide ? styles.main : styles.bar}
        role="region"
        aria-label={C.pay(formatPaise(cart!.totalInPaise))}
      >
        {message ? (
          <InlineMessage
            tone={message.tone}
            detail={message.ref ? `Reference: ${message.ref}` : null}
            action={
              message.orders ? (
                <Link href="/orders" asChild>
                  <Button variant="outline" accessibilityRole="link">
                    {C.goToOrders}
                  </Button>
                </Link>
              ) : undefined
            }
          >
            {message.text}
          </InlineMessage>
        ) : null}
        {paying === "confirming" ? (
          <InlineMessage tone="info">{C.confirming}</InlineMessage>
        ) : null}
        {blocked && paying === "idle" ? (
          <Text size="sm" tone="muted">
            {blocked}
          </Text>
        ) : null}
        <Button
          pending={paying !== "idle"}
          disabled={blocked !== null}
          onPress={() => void startPay()}
          testID="pay"
        >
          {C.pay(formatPaise(cart!.totalInPaise))}
        </Button>
        <Text size="xs" tone="muted">
          {C.secure(null)}
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.root} testID="cart-screen">
      <ScrollView style={styles.root}>
        <View style={styles.page} role="main">
          {/* Phones: the app bar carries the page heading. */}
          {wide ? (
            <Text variant="display" accessibilityRole="header" aria-level={1}>
              {C.title}
            </Text>
          ) : null}
          {wide ? (
            <View style={styles.steps} role="list" aria-label="Checkout steps">
              {C.steps.map((s, i) => (
                <Text
                  key={s}
                  weight={i < 2 ? "bold" : "regular"}
                  tone={i < 2 ? "default" : "muted"}
                  role="listitem"
                >
                  {`${i + 1} ${s}`}
                </Text>
              ))}
            </View>
          ) : null}
          {wide ? (
            <View style={styles.columns}>
              {children}
              {side}
            </View>
          ) : (
            <>
              {children}
              {side}
            </>
          )}
        </View>
      </ScrollView>
      {!wide && cart.itemCount > 0 ? payBlock() : null}
      <FakePaymentSheet />
    </View>
  );
}
